import { ASSET_STUDIO } from '@/types/shared/navigation-tabs.enums';
import React, { ReactNode, useEffect, useState } from 'react';
import { ComponentCard } from './ComponentCard';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { AssetHealthStatusIndicator } from '@/components/pages/AssetsPreviewPage/shared/AssetHealthStatusIndicator';
import SubComponentCard from './SubComponentCard';
import { BaseTooltip } from '../Tooltip';
import { MaintenanceCard } from './MaintenanceCard';
import { HealthStatusIndicator } from '@/components/shared/display/StateIndicator';
import { FaultReasons } from '@/modules';
import { healthStatusFlagsToString } from '@/utils/shared';

interface UIUnitsAccordionProps {
  renderItemsCard?: (item: any) => ReactNode;
  group?: {
    menuLabel?: string;
    menuItems: any[];
  };
  isSelected: (groupLabel: string, item: string) => boolean;
  toggleItem: any;
  uiUnits: string[];
  onClick?: (el: any) => void;
  groupKey?: string;
  variant?: 'base' | 'subcomponents' | 'maintenance' | 'subcomponents-enabled';
}
// @TODO:refactor duplicated code (start&end block)
export function UIUnitsAccordion({
  renderItemsCard,
  group,
  isSelected,
  toggleItem,
  uiUnits,
  groupKey = 'unit_ui_name',
  onClick,
  variant,
}: UIUnitsAccordionProps) {
  if (!group) return null;
  if (!uiUnits || uiUnits.length === 0) return null;
  const subItemsHealthStatus: any = {};
  group?.menuItems.forEach((el: any) => {
    subItemsHealthStatus[`${el.subcomponent_ui_name}`] = el.subcomponent_health_status;
  });
  //TODO: refactor this to add the metadata as a prop
  const unitsMeta: any = {};
  for (const unit of uiUnits) {
    unitsMeta[unit] = {};
    const item = group?.menuItems.find((el: any) => el[groupKey] === unit && !el.isCustomAnalysis);
    if (item) unitsMeta[unit].component = item?.component || null;
  }

  // Find initially selected accordions
  const initiallyOpen = uiUnits.filter((el) =>
    group?.menuItems.some((menuItem) => menuItem[groupKey] === el && isSelected(group.menuLabel!, menuItem.id))
  );

  // State to track open accordions
  const [openUnits, setOpenUnits] = useState<string[]>(initiallyOpen);

  // Keep state in sync if selections change
  useEffect(() => {
    const selectedUnits = uiUnits.filter((el) =>
      group?.menuItems.some((menuItem) => menuItem[groupKey] === el && isSelected(group.menuLabel!, menuItem.id))
    );
    setOpenUnits(selectedUnits);
  }, [group, uiUnits, isSelected]);

  // Handle toggling accordions
  const handleAccordionToggle = (value: string) => {
    setOpenUnits((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  return (
    <>
      {variant !== 'subcomponents-enabled' &&
        group?.menuItems
          ?.filter((menuItem: any) => [ASSET_STUDIO.ALL_COMPONENTS, ASSET_STUDIO.ALL_FORMULAS].includes(menuItem.id))
          .map((component, index) => {
            return renderItemsCard ? (
              <React.Fragment key={component?.id || index}>{renderItemsCard(component)}</React.Fragment>
            ) : (
              <React.Fragment key={component?.id || index}>
                <ComponentCard
                  item={component}
                  isSelected={isSelected(group.menuLabel!, component.id)}
                  onClick={() => {
                    toggleItem(group.menuLabel!, component);
                    // Delay drawer opening to allow selected indicator to show first
                    if (onClick) {
                      requestAnimationFrame(() => {
                        onClick(component);
                      });
                    }
                  }}
                />
              </React.Fragment>
            );
          })}
      <Accordion type="multiple" value={openUnits ?? undefined}>
        {uiUnits.map((el) => (
          <AccordionItem key={el} value={el} className="p-0">
            <AccordionTrigger
              onClick={() => handleAccordionToggle(el)}
              className={cn(
                'w-full',
                'bg-card mb-2 pl-2 pr-4 py-3 rounded-lg hover:no-underline text-sm text-left border border-gray-300 dark:border-slate-600',
                'flex items-center justify-between gap-2',
                '[&>svg]:scale-125',
                variant === 'subcomponents-enabled'
                  ? 'bg-gray-100 dark:bg-[#2f3844] rounded-none mb-0 [&>svg]:rotate-[-90deg] [&[data-state=open]>svg]:rotate-[0deg]'
                  : unitsMeta[el]?.component?.health_status === 'na'
                  ? 'bg-neutral-25 dark:bg-neutral-800 ring-inset ring-[0.25rem] ring-white dark:ring-gray-700'
                  : ''
              )}
            >
              {variant === 'subcomponents-enabled' ? (
                <div className="flex items-center gap-2 flex-grow min-w-0">
                  <BaseTooltip content={el}>
                    <div className="flex items-center min-w-0 flex-grow">
                      <span className="block truncate text-inherit whitespace-nowrap overflow-hidden min-w-0 flex-grow">
                        {el}
                      </span>
                    </div>
                  </BaseTooltip>
                  <AssetHealthStatusIndicator healthStatus={subItemsHealthStatus[el]} />
                </div>
              ) : (
                <span className="block truncate text-inherit whitespace-nowrap overflow-hidden min-w-0 flex-grow">
                  {el}
                </span>
              )}
            </AccordionTrigger>

            <AccordionContent>
              <div
                className={` flex flex-col ${variant !== 'subcomponents-enabled' ? 'pb-2 ml-4 pr-1 gap-2 white' : ''}`}
              >
                {group?.menuItems
                  ?.filter((menuItem: any) => menuItem[groupKey] === el)
                  .map((component: any, index) => {
                    return renderItemsCard ? (
                      <React.Fragment key={component?.id || index}>{renderItemsCard(component)}</React.Fragment>
                    ) : (
                      <React.Fragment key={component?.id || index}>
                        {variant === 'subcomponents-enabled' ? (
                          <>
                            <SubComponentCard
                              title={component.name}
                              health_status={component.health_status}
                              isSelected={isSelected(group.menuLabel!, component.id)}
                              onClick={() => {
                                toggleItem(group.menuLabel!, component);
                                onClick && onClick(component);
                              }}
                            />
                          </>
                        ) : variant === 'maintenance' ? (
                          <MaintenanceCard item={component} isSelected={isSelected(group.menuLabel!, component.id)} />
                        ) : (
                          <ComponentCard
                            showHealthStatus={true}
                            item={component}
                            isSelected={isSelected(group.menuLabel!, component.id)}
                            onClick={() => {
                              toggleItem(group.menuLabel!, component);
                              onClick && onClick(component);
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      {group?.menuItems
        ?.filter((menuItem: any) => !menuItem[groupKey] && ![ASSET_STUDIO.ALL_COMPONENTS].includes(menuItem.id))
        .map((component: any, index) => {
          return renderItemsCard ? (
            <React.Fragment key={component?.id || index}>{renderItemsCard(component)}</React.Fragment>
          ) : (
            <React.Fragment key={component?.id || index}>
              {variant === 'subcomponents-enabled' ? (
                <SubComponentCard
                  classNames={{ container: 'pt-1 border-none font-semibold' }}
                  title={component.name}
                  isSelected={isSelected(group.menuLabel!, component.id)}
                  onClick={() => {
                    toggleItem(group.menuLabel!, component);
                    // Delay drawer opening to allow selected indicator to show first
                    if (onClick) {
                      requestAnimationFrame(() => {
                        onClick(component);
                      });
                    }
                  }}
                />
              ) : (
                <ComponentCard
                  item={component}
                  isSelected={isSelected(group.menuLabel!, component.id)}
                  onClick={() => {
                    toggleItem(group.menuLabel!, component);
                    // Delay drawer opening to allow selected indicator to show first
                    if (onClick) {
                      requestAnimationFrame(() => {
                        onClick(component);
                      });
                    }
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
    </>
  );
}
